// 下記の API で返却されるような、 kintone のフィールド情報を表す型。
// 当面必要なプロパティに絞って定義しておく。
// https://cybozu.dev/ja/kintone/docs/rest-api/apps/form/get-form-fields/
export type KintoneFormField =
  | KintoneCommonFormField
  | KintoneSubtableFormField;

export type KintoneFormFields = Record<
  KintoneFormField["code"],
  KintoneFormField
>;

export type KintoneCommonFormField =
  | {
      code: string;
      label: string;
      type: string;
    }
  | KintoneSupportedFormField;

// スマート申請の kintone 連携機能でサポートしている入力項目群。
// SUBTABLE は別枠とする。
export type KintoneSupportedFormField =
  | KintoneSingleLineTextFormField
  | KintoneMultiLineTextFormField
  | KintoneNumberFormField
  | KintoneCheckBoxFormField
  | KintoneRadioButtonFormField
  | KintoneDropDownFormField
  | KintoneDateFormField
  | KintoneTimeFormField
  | KintoneFileFormField
  | KintoneMultiSelectFormField;

export type KintoneSingleLineTextFormField = {
  code: string;
  label: string;
  type: "SINGLE_LINE_TEXT";
};

export type KintoneMultiLineTextFormField = {
  code: string;
  label: string;
  type: "MULTI_LINE_TEXT";
};

export type KintoneNumberFormField = {
  code: string;
  label: string;
  type: "NUMBER";
};

export type KintoneCheckBoxFormField = {
  code: string;
  label: string;
  type: "CHECK_BOX";
  options: KintoneSelectableOptions;
};

export type KintoneRadioButtonFormField = {
  code: string;
  label: string;
  type: "RADIO_BUTTON";
  options: KintoneSelectableOptions;
};

export type KintoneDropDownFormField = {
  code: string;
  label: string;
  type: "DROP_DOWN";
  options: KintoneSelectableOptions;
};

export type KintoneDateFormField = {
  code: string;
  label: string;
  type: "DATE";
};

export type KintoneTimeFormField = {
  code: string;
  label: string;
  type: "TIME";
};

export type KintoneFileFormField = {
  code: string;
  label: string;
  type: "FILE";
};

export type KintoneMultiSelectFormField = {
  code: string;
  label: string;
  type: "MULTI_SELECT";
  options: KintoneSelectableOptions;
};

export type KintoneSelectableOptions = Record<
  string,
  { index: string; label: string }
>;

export type KintoneSubtableFormField = {
  code: string;
  label: string;
  type: "SUBTABLE";
  fields: Record<KintoneCommonFormField["code"], KintoneCommonFormField>;
};

export const isKintoneSubtableFormField = (
  field: KintoneFormField,
): field is KintoneSubtableFormField => {
  return field.type === "SUBTABLE";
};

// https://cybozu.dev/ja/kintone/docs/overview/field-types/#field-type を参考に、
// field type を日本語のフィールド種類名に変換する。
export const kintoneFieldReadableTypeName = (
  fieldType: KintoneFormField["type"],
): string => {
  switch (fieldType) {
    case "SINGLE_LINE_TEXT":
      return "文字列（1行）";
    case "MULTI_LINE_TEXT":
      return "文字列（複数行）";
    case "NUMBER":
      return "数値";
    case "CHECK_BOX":
      return "チェックボックス";
    case "RADIO_BUTTON":
      return "ラジオボタン";
    case "DROP_DOWN":
      return "ドロップダウン";
    case "DATE":
      return "日付";
    case "TIME":
      return "時刻";
    case "DATETIME":
      return "日時";
    case "FILE":
      return "添付ファイル";
    case "MULTI_SELECT":
      return "複数選択";
    case "SUBTABLE":
      return "テーブル";
    default:
      return fieldType;
  }
};

export type KintoneRecord = Record<KintoneFormField["code"], KintoneColumn>;

export type KintoneColumn = { value: KintoneColumnValue };

export type KintoneColumnValue =
  | KintoneColumnLeafValue
  | KintoneColumnSubtableValue;

export type KintoneColumnLeafValue = string | string[] | KintoneColumnFileValue;

export type KintoneColumnFileValue = [{ fileKey: string }];

export type KintoneColumnSubtableValue = {
  value: Record<
    KintoneCommonFormField["code"],
    { value: KintoneColumnLeafValue }
  >;
}[];

export const isKintoneColumnFileValue = (
  value: KintoneColumnValue,
): value is KintoneColumnFileValue => {
  if (!Array.isArray(value)) {
    return false;
  }

  // graffer schema との対応関係的にファイル項目の配列要素は一つのみ。
  if (value.length !== 1) {
    return false;
  }

  return typeof value[0] === "object" && "fileKey" in value[0];
};

export const isKintoneColumnSubtableValue = (
  value: KintoneColumnValue,
): value is KintoneColumnSubtableValue => {
  return (
    Array.isArray(value) &&
    value.every(
      (v) =>
        typeof v === "object" && "value" in v && typeof v.value === "object",
    )
  );
};
